/** @jsxImportSource @emotion/react */
import React from "react";
import Form from "react-bootstrap/Form";
import TextField from "../../../Utils/FormInputs/TextField";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import Label from "../../../Utils/FormInputs/Label";
import {
    PNCFormGrid,
    PNCCheckboxGrid, PNCCheckboxItem,
    PNCToggle, PNCToggleSwitch, PNCToggleLabel,
    PNCPrimaryBtn, PNCSecondaryBtn, PNCButtonRow,
    PNCAlertContainer, PNCAlertCard, PNCAlertContent,
    PNCAlertTitle, PNCAlertDetail, PNCAlertAction,
    PNCSectionDivider, PNCSectionLine, PNCSectionLabel,
    PNCHint,
} from "./PostnatalCareStyles";
import CustomDialog from "../../../Utils/Dialogs/CustomDialog";
import { EdinburghDepressionScale } from "./EdinburghDepressionScale";

/* ═══════════════════════════════════════════════════════════════
   PostnatalMaternalForm — Maternal PNC Assessment Form
   ═══════════════════════════════════════════════════════════════ */

const toSelectOptions = function (items) {
    return items.map(function (m) { return { value: m.value, text: m.label }; });
};

/* ═══ MAIN COMPONENT ═══ */
export var PostnatalMaternalForm = function (props) {
    var maternal = props.maternal;

    var form = maternal.form;
    var handleChange = maternal.handleChange;
    var toggleComplication = maternal.toggleComplication;
    var alerts = maternal.alerts;
    var pncVisits = maternal.pncVisits;
    var editingVisit = maternal.editingVisit;
    var saving = maternal.saving;
    var savePncVisit = maternal.savePncVisit;
    var resetForm = maternal.resetForm;
    var showEpds = maternal.showEpds;
    var setShowEpds = maternal.setShowEpds;

    /* Build select options */
    var visitOptions = maternal.PNC_VISIT_LABELS.map(function (v) {
        var done = pncVisits.some(function (pv) { return pv.pnc_visit_number === v.number; });
        return { value: String(v.number), text: v.label + (done ? " (done)" : "") };
    });
    var uterineOptions = toSelectOptions(maternal.UTERINE_INVOLUTION);
    var lochiaOptions = toSelectOptions(maternal.LOCHIA_STATUS);
    var perineumOptions = toSelectOptions(maternal.PERINEUM_STATUS);
    var breastOptions = toSelectOptions(maternal.BREAST_STATUS);
    var bfOptions = toSelectOptions(maternal.BREASTFEEDING_STATUS);
    var fpOptions = [
        { value: "coc", text: "Combined Oral Contraceptive (COC)" },
        { value: "pop", text: "Progestogen-Only Pill (POP)" },
        { value: "dmpa", text: "DMPA Injectable (Depo-Provera)" },
        { value: "net_en", text: "NET-EN Injectable" },
        { value: "jadelle", text: "Jadelle Implant" },
        { value: "implanon", text: "Implanon Implant" },
        { value: "copper_iud", text: "Copper IUD" },
        { value: "lng_ius", text: "LNG-IUS (Mirena)" },
        { value: "male_condom", text: "Male Condom" },
        { value: "female_condom", text: "Female Condom" },
        { value: "lam", text: "LAM (Lactational Amenorrhea)" },
        { value: "tubal_ligation", text: "Bilateral Tubal Ligation" },
        { value: "counselled_only", text: "Counselled — Method not yet chosen" },
    ];

    return (
        <div>
            {/* ─── EPDS Dialog ─── */}
            <CustomDialog
                open={showEpds}
                handleClose={function () { setShowEpds(false); }}
                title="Edinburgh Postnatal Depression Scale (EPDS)"
                maxWidth="md"
            >
                <EdinburghDepressionScale
                    questions={maternal.EPDS_QUESTIONS}
                    responses={maternal.epdsResponses}
                    onResponse={maternal.handleEpdsResponse}
                    score={maternal.epdsScore}
                    isComplete={maternal.epdsComplete}
                    interpretation={maternal.epdsInterpretation}
                    onApply={function () { maternal.applyEpdsScore(); setShowEpds(false); }}
                    onCancel={function () { setShowEpds(false); }}
                />
            </CustomDialog>
            {/* ─── Alerts ─── */}
            {alerts.length > 0 && (
                <PNCAlertContainer>
                    {alerts.map(function (alert, idx) {
                        return (
                            <PNCAlertCard key={idx} severity={alert.type}>
                                <PNCAlertContent>
                                    <PNCAlertTitle>{alert.title}</PNCAlertTitle>
                                    <PNCAlertDetail>{alert.detail}</PNCAlertDetail>
                                    {alert.action && <PNCAlertAction>{alert.action}</PNCAlertAction>}
                                </PNCAlertContent>
                            </PNCAlertCard>
                        );
                    })}
                </PNCAlertContainer>
            )}

            {/* ─── Header: Edit / New ─── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#333" }}>
                    {editingVisit ? "Edit PNC Visit " + editingVisit.pnc_visit_number : "New PNC Assessment"}
                </h3>
                {editingVisit && (
                    <PNCSecondaryBtn onClick={resetForm} style={{ padding: "5px 12px", fontSize: "0.78rem" }}>
                        Cancel Edit
                    </PNCSecondaryBtn>
                )}
            </div>

            {/* ─── Visit Number & Date ─── */}
            <PNCFormGrid cols={3}>
                <Form.Group>
                    <Label name="PNC Visit Number" type />
                    <SelectInput
                        name="pnc_visit_number"
                        value={form.pnc_visit_number}
                        onChange={handleChange}
                        options={visitOptions}
                        defaultOption="— Select —"
                    />
                    {form.pnc_visit_number && (
                        <PNCHint>
                            {(maternal.PNC_VISIT_LABELS.find(function (v) { return v.number === parseInt(form.pnc_visit_number); }) || {}).timing}
                        </PNCHint>
                    )}
                </Form.Group>
                <Form.Group>
                    <Label name="Visit Date" type />
                    <TextField type="date" name="visit_date" value={form.visit_date} onChange={handleChange} />
                </Form.Group>
                <Form.Group>
                    <Label name="Next Visit Date" />
                    <TextField type="date" name="next_visit_date" value={form.next_visit_date} onChange={handleChange} />
                </Form.Group>
            </PNCFormGrid>

            {/* ─── Vital Signs ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Vital Signs</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <PNCFormGrid cols={4}>
                <Form.Group>
                    <Label name="Systolic BP (mmHg)" />
                    <TextField type="number" name="bp_systolic" value={form.bp_systolic}
                        onChange={handleChange} placeholder="120" min="60" max="250" />
                    {parseFloat(form.bp_systolic) >= 140 && (
                        <PNCHint warn>Elevated ({form.bp_systolic} mmHg)</PNCHint>
                    )}
                </Form.Group>
                <Form.Group>
                    <Label name="Diastolic BP (mmHg)" />
                    <TextField type="number" name="bp_diastolic" value={form.bp_diastolic}
                        onChange={handleChange} placeholder="80" min="30" max="160" />
                    {parseFloat(form.bp_diastolic) >= 90 && (
                        <PNCHint warn>Elevated ({form.bp_diastolic} mmHg)</PNCHint>
                    )}
                </Form.Group>
                <Form.Group>
                    <Label name="Pulse (bpm)" />
                    <TextField type="number" name="pulse" value={form.pulse}
                        onChange={handleChange} placeholder="78" min="40" max="200" />
                </Form.Group>
                <Form.Group>
                    <Label name="Temperature (°C)" />
                    <TextField type="number" name="temperature" value={form.temperature}
                        onChange={handleChange} placeholder="36.5" step="0.1" min="34" max="42" />
                    {parseFloat(form.temperature) >= 38 && (
                        <PNCHint warn>Fever — investigate sepsis</PNCHint>
                    )}
                </Form.Group>
            </PNCFormGrid>

            {/* ─── Uterine & Lochia Assessment ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Uterine & Lochia Assessment</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <PNCFormGrid cols={3}>
                <Form.Group>
                    <Label name="Uterine Involution" />
                    <SelectInput
                        name="uterine_involution"
                        value={form.uterine_involution}
                        onChange={handleChange}
                        options={uterineOptions}
                        defaultOption="— Select —"
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Lochia" />
                    <SelectInput
                        name="lochia"
                        value={form.lochia}
                        onChange={handleChange}
                        options={lochiaOptions}
                        defaultOption="— Select —"
                    />
                    {form.lochia === "offensive" && (
                        <PNCHint warn>Possible endometritis — start antibiotics</PNCHint>
                    )}
                </Form.Group>
                <Form.Group>
                    <Label name="Perineum / Wound" />
                    <SelectInput
                        name="perineum_wound"
                        value={form.perineum_wound}
                        onChange={handleChange}
                        options={perineumOptions}
                        defaultOption="— Select —"
                    />
                </Form.Group>
            </PNCFormGrid>

            {/* DVT Toggle */}
            <div style={{ marginTop: "12px" }}>
                <PNCToggle checked={form.dvt_signs}
                    onClick={function () {
                        handleChange({ target: { name: "dvt_signs", type: "checkbox", checked: !form.dvt_signs } });
                    }}>
                    <PNCToggleSwitch checked={form.dvt_signs} />
                    <PNCToggleLabel checked={form.dvt_signs}>
                        Signs of DVT present (calf pain, swelling, Homan's sign)
                    </PNCToggleLabel>
                </PNCToggle>
            </div>

            {/* ─── Breasts & Breastfeeding ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Breasts & Breastfeeding</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <PNCFormGrid cols={3}>
                <Form.Group>
                    <Label name="Breast Examination" />
                    <SelectInput
                        name="breasts"
                        value={form.breasts}
                        onChange={handleChange}
                        options={breastOptions}
                        defaultOption="— Select —"
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Breastfeeding Status" />
                    <SelectInput
                        name="breastfeeding_status"
                        value={form.breastfeeding_status}
                        onChange={handleChange}
                        options={bfOptions}
                        defaultOption="— Select —"
                    />
                </Form.Group>
                <Form.Group>
                    <Label name="Haemoglobin (g/dL)" />
                    <TextField type="number" name="hb_if_checked" value={form.hb_if_checked}
                        onChange={handleChange} placeholder="12.0" step="0.1" min="3" max="20" />
                    {parseFloat(form.hb_if_checked) > 0 && parseFloat(form.hb_if_checked) < 11 && (
                        <PNCHint warn>Anaemia (Hb &lt; 11 g/dL)</PNCHint>
                    )}
                </Form.Group>
            </PNCFormGrid>

            {/* ─── Depression Screening ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Depression Screening (EPDS)</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>
            <PNCFormGrid cols={2}>
                <Form.Group>
                    <Label name="EPDS Score" />
                    <TextField type="number" name="depression_screening_score"
                        value={form.depression_screening_score !== null && form.depression_screening_score !== undefined ? form.depression_screening_score : ""}
                        onChange={handleChange} placeholder="Enter score or use calculator"
                        min="0" max="30" />
                    <PNCHint>Range: 0–30. Enter directly or use the EPDS calculator below.</PNCHint>
                </Form.Group>
                <Form.Group style={{ display: "flex", justifyContent: "center", alignItems: "flex-end" }}>
                    <button className="btn sms-btn-outline"
                        onClick={function (e) { e.preventDefault(); setShowEpds(true); }}
                    >
                        Open EPDS Calculator
                    </button>
                </Form.Group>
            </PNCFormGrid>

            {/* ─── Complications ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Complications</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <PNCCheckboxGrid cols={3}>
                {maternal.PNC_COMPLICATION_OPTIONS.map(function (comp) {
                    var checked = form.complications.indexOf(comp.value) >= 0;
                    var isDanger = comp.value !== "none";
                    return (
                        <PNCCheckboxItem key={comp.value} checked={checked} danger={isDanger && checked}
                            onClick={function () { toggleComplication(comp.value); }}>
                            <input type="checkbox" checked={checked} readOnly
                                style={{ width: "16px", height: "16px", accentColor: isDanger ? "#c62828" : "#00695c" }} />
                            <span>{comp.label}</span>
                        </PNCCheckboxItem>
                    );
                })}
            </PNCCheckboxGrid>

            {/* ─── Interventions & Medications ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Interventions & Medications</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <PNCToggle checked={form.iron_folate_given}
                    onClick={function () {
                        handleChange({ target: { name: "iron_folate_given", type: "checkbox", checked: !form.iron_folate_given } });
                    }}>
                    <PNCToggleSwitch checked={form.iron_folate_given} />
                    <PNCToggleLabel checked={form.iron_folate_given}>
                        Iron & Folic Acid given
                    </PNCToggleLabel>
                </PNCToggle>

                <PNCToggle checked={form.vitamin_a_given}
                    onClick={function () {
                        handleChange({ target: { name: "vitamin_a_given", type: "checkbox", checked: !form.vitamin_a_given } });
                    }}>
                    <PNCToggleSwitch checked={form.vitamin_a_given} />
                    <PNCToggleLabel checked={form.vitamin_a_given}>
                        Vitamin A 200,000 IU given (within 8 weeks postpartum)
                    </PNCToggleLabel>
                </PNCToggle>

                <PNCToggle checked={form.art_continuation}
                    onClick={function () {
                        handleChange({ target: { name: "art_continuation", type: "checkbox", checked: !form.art_continuation } });
                    }}>
                    <PNCToggleSwitch checked={form.art_continuation} />
                    <PNCToggleLabel checked={form.art_continuation}>
                        ART Continuation (for HIV-positive mothers)
                    </PNCToggleLabel>
                </PNCToggle>

                <PNCToggle checked={form.cervical_screening_referral}
                    onClick={function () {
                        handleChange({ target: { name: "cervical_screening_referral", type: "checkbox", checked: !form.cervical_screening_referral } });
                    }}>
                    <PNCToggleSwitch checked={form.cervical_screening_referral} />
                    <PNCToggleLabel checked={form.cervical_screening_referral}>
                        Referred for Cervical Cancer Screening (at 6-week visit)
                    </PNCToggleLabel>
                </PNCToggle>
            </div>

            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Family Planning</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <Form.Group>
                <Label name="Family Planning Method Started" />
                <SelectInput
                    name="fp_method_started"
                    value={form.fp_method_started}
                    onChange={handleChange}
                    options={fpOptions}
                    defaultOption="— None / Not yet —"
                />
                <PNCHint>Document FP counselling and method initiation at 6-week PNC visit</PNCHint>
            </Form.Group>

            {/* ─── Clinical Notes ─── */}
            <PNCSectionDivider>
                <PNCSectionLine />
                <PNCSectionLabel>Clinical Notes</PNCSectionLabel>
                <PNCSectionLine />
            </PNCSectionDivider>

            <Form.Group>
                <Label name="Notes / Plan" />
                <ResizableTextarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Clinical findings, counselling provided, follow-up plan..."
                />
            </Form.Group>

            {/* ─── Save Buttons ─── */}
            <PNCButtonRow>
                {editingVisit && (
                    <PNCSecondaryBtn onClick={resetForm}>Cancel Edit</PNCSecondaryBtn>
                )}
                <button className="btn sms-info-btn" disabled={saving} onClick={savePncVisit}>
                    {saving ? "Saving..." : editingVisit ? "Update PNC Visit" : "Save PNC Visit"}
                </button>
            </PNCButtonRow>

        </div>
    );
};
